// ────────────────────────────────────────────────────────────────────
// DeskScreen — the tiled work surface for one asset (workbench roadmap C1/C2).
//
// Route: /desk/:assetId (a path param, matching every other single-asset
// route in this app — /assets/:assetId, /sites/:plantId — rather than a
// query param on /assets/:assetId?view=desk. The Desk is a DIFFERENT
// surface from the client-facing asset workspace (different audience,
// different chrome, staff-only entry point), not a view toggle on the same
// screen, so it gets its own route the same way /review/:fileId does for
// the Review Room.
//
// Layout (fixed preset, no customization — spec is explicit: MDI free-
// floating layouts are a tax users pay forever):
//   ≥1100px: [ CML table ] [ selected CML history+trend / calculator ]
//   <1100px: stacked, table on top.
// One shared focus (useWorkspaceFocus "cml") drives every panel — the line
// between a desk and a set of tabs.
// ────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, RotateCcw, TriangleAlert } from "lucide-react";
import { apiClient } from "@/lib/api";
import type { CmlRow, WhatIfResult } from "@/lib/types";
import { useWorkspaceFocus } from "@/components/WorkspaceContext";
import { RemainingLifeProjection, type WhatIfProjection } from "@/components/RemainingLifeProjection";
import { dqTooltip } from "@/lib/dqFlags";
import { useIsNarrow } from "@/lib/useIsNarrow";
import { useRecordRecentDesk } from "@/lib/recentDesks";

const fmtMm = (n: number | null): string => (n != null ? n.toFixed(2) : "—");
const fmtRate = (n: number | null): string => (n != null ? n.toFixed(3) : "—");
const fmtYr = (n: number | null): string => (n == null ? "—" : n >= 100 ? ">100" : n.toFixed(1));
/** undefined = "not set" (leave the field to the server default); NaN/blank
 *  never reach the request body — a mistyped input degrades to "unset",
 *  never to a silently-wrong 0 or null-via-JSON.stringify(NaN). */
const parseNum = (s: string): number | undefined => {
  if (s.trim() === "") return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

function cmlMargin(c: CmlRow): number | null {
  if (c.t_actual_mm == null || !c.t_min_mm || c.t_min_mm <= 0) return null;
  return c.t_actual_mm - c.t_min_mm;
}

type SortMode = "margin" | "cml" | "flagged";
const SORT_LABEL: Record<SortMode, string> = { margin: "Margin ต่ำสุดก่อน", cml: "เรียงตาม CML", flagged: "ค่าที่น่าสงสัยก่อน" };

function sortCmls(cmls: CmlRow[], mode: SortMode): CmlRow[] {
  const arr = [...cmls];
  if (mode === "cml") return arr.sort((a, b) => a.cml_no.localeCompare(b.cml_no, undefined, { numeric: true }));
  if (mode === "flagged") {
    return arr.sort((a, b) => {
      const fa = (a.dq?.length ?? 0) > 0 ? 0 : 1, fb = (b.dq?.length ?? 0) > 0 ? 0 : 1;
      if (fa !== fb) return fa - fb;
      const ma = cmlMargin(a), mb = cmlMargin(b);
      if (ma == null) return mb == null ? 0 : 1;
      if (mb == null) return -1;
      return ma - mb;
    });
  }
  // default: worst margin first — the number an engineer decides on. Unknown
  // margins (no t-min / no reading) sort last, not first (never look "urgent").
  return arr.sort((a, b) => {
    const ma = cmlMargin(a), mb = cmlMargin(b);
    if (ma == null) return mb == null ? 0 : 1;
    if (mb == null) return -1;
    return ma - mb;
  });
}

export default function DeskScreen(): JSX.Element {
  const { assetId } = useParams<{ assetId: string }>();
  const id = Number(assetId);
  useRecordRecentDesk(Number.isFinite(id) ? id : null);
  const navigate = useNavigate();
  const { setFocus } = useWorkspaceFocus();
  const narrow = useIsNarrow(1100);

  const assetQ = useQuery({ queryKey: ["asset", id], queryFn: () => apiClient.asset(id), enabled: !!id });
  const corrosionQ = useQuery({ queryKey: ["corrosion", id], queryFn: () => apiClient.assetCorrosion(id), enabled: !!id });
  const asset = assetQ.data;
  const corrosion = corrosionQ.data;

  const [sortMode, setSortMode] = useState<SortMode>("margin");
  const [selCmlId, setSelCmlId] = useState<number | null>(null);

  // Same governing-CML-first fallback AssetWorkspaceScreen uses, so opening
  // the Desk lands on the CML that actually needs attention, not CML #1.
  const selCml = useMemo(() => {
    if (!corrosion) return null;
    const explicit = corrosion.cmls.find((c) => c.cml_id === selCmlId);
    if (explicit) return explicit;
    const govNo = corrosion.rollup.governing?.cml_no;
    return (govNo ? corrosion.cmls.find((c) => c.cml_no === govNo) : undefined)
      ?? sortCmls(corrosion.cmls, "margin")[0]
      ?? null;
  }, [corrosion, selCmlId]);

  // First real use of the "cml" workspace focus type (roadmap C1) — the
  // notes dock and any future desk-aware surface read this.
  useEffect(() => {
    if (selCml) setFocus({ type: "cml", ref: String(selCml.cml_id), label: selCml.cml_no });
    return () => setFocus(null);
  }, [selCml, setFocus]);

  if (assetQ.isPending || corrosionQ.isPending) return <CenterNote>กำลังโหลด…</CenterNote>;
  if (assetQ.isError || !asset) return <CenterNote tone="err">ไม่พบ asset</CenterNote>;

  const cmls = corrosion ? sortCmls(corrosion.cmls, sortMode) : [];

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden" style={{ background: "var(--paper)" }}>
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="px-6 h-12 border-b flex items-center justify-between shrink-0" style={{ borderColor: "var(--hairline)" }}>
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate(-1)} title="ย้อนกลับ" aria-label="ย้อนกลับ"
                  className="p-1 hover:text-[var(--accent)]" style={{ color: "var(--ink-3)" }}>
            <ArrowLeft size={15} />
          </button>
          <span className="font-mono uppercase" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
            / โต๊ะทำงาน
          </span>
          <span className="font-mono" style={{ fontSize: 15, color: "var(--accent-deep)", fontWeight: 700 }}>{asset.tag}</span>
          {asset.name && asset.name !== asset.tag && (
            <span className="font-mono truncate" style={{ fontSize: 12, color: "var(--ink-3)" }}>{asset.name}</span>
          )}
        </div>
      </div>

      {/* ── Tiled body ───────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-auto"
           style={{ display: narrow ? "flex" : "grid", flexDirection: narrow ? "column" : undefined,
                    gridTemplateColumns: narrow ? undefined : "minmax(360px,1fr) minmax(480px,1.5fr)" }}>
        {/* LEFT — CML table */}
        <div className="p-4 border-r min-w-0" style={{ borderColor: narrow ? "transparent" : "var(--hairline)", borderBottom: narrow ? "1px solid var(--hairline)" : "none" }}>
          <div className="flex items-center justify-between mb-2 gap-2">
            <span className="font-mono uppercase" style={{ fontSize: 9.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>
              CMLs · {cmls.length}
            </span>
            <div className="flex" style={{ border: "1px solid var(--hairline)" }}>
              {(["margin", "cml", "flagged"] as SortMode[]).map((m) => (
                <button key={m} onClick={() => setSortMode(m)} title={SORT_LABEL[m]}
                        className="font-mono uppercase px-2 py-0.5 transition-colors"
                        style={{ fontSize: 8, letterSpacing: ".06em",
                                 background: sortMode === m ? "var(--accent)" : "transparent",
                                 color: sortMode === m ? "#fff" : "var(--ink-3)" }}>
                  {m === "margin" ? "MARGIN" : m === "cml" ? "CML" : "FLAG"}
                </button>
              ))}
            </div>
          </div>
          <DeskCmlTable cmls={cmls} govCml={corrosion?.rollup.governing?.cml_no}
                        selId={selCml?.cml_id} onSelect={(c) => setSelCmlId(c.cml_id)} />
        </div>

        {/* RIGHT — history/trend (top) + calculator (bottom) */}
        <div className="flex flex-col min-w-0">
          <div className="p-4 border-b min-h-0" style={{ borderColor: "var(--hairline)" }}>
            {selCml ? (
              <CmlHistoryPanel c={selCml} nextDue={corrosion?.rollup.next_inspection_due} />
            ) : (
              <div className="p-6 text-center font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
                เลือก CML ทางซ้ายเพื่อดูประวัติ
              </div>
            )}
          </div>
          <div className="p-4 flex-1 min-h-0">
            {selCml ? <Calculator cml={selCml} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── LEFT — CML table ──
const GRID = "minmax(80px,1fr) 60px 56px 62px 60px 52px 22px";

function DeskCmlTable({ cmls, govCml, selId, onSelect }: {
  cmls: CmlRow[]; govCml?: string; selId?: number; onSelect: (c: CmlRow) => void;
}): JSX.Element {
  if (cmls.length === 0) {
    return <div className="font-mono text-xs py-4" style={{ color: "var(--ink-3)" }}>ไม่มี CML สำหรับ asset นี้</div>;
  }
  return (
    <div className="border" style={{ borderColor: "var(--hairline)" }}>
      <div className="grid items-center gap-1.5 px-2.5 py-2 border-b font-mono uppercase"
           style={{ gridTemplateColumns: GRID, fontSize: 8, letterSpacing: ".08em", color: "var(--ink-4)", background: "var(--paper-2)", borderColor: "var(--hairline)" }}>
        <span>CML</span><span className="text-right">ล่าสุด</span><span className="text-right">t-min</span>
        <span className="text-right">margin</span><span className="text-right">CR</span><span className="text-right">RL</span><span />
      </div>
      <div className="overflow-auto" style={{ maxHeight: 560 }}>
        {cmls.map((c) => {
          const margin = cmlMargin(c);
          const marginColor = margin == null ? "var(--ink-4)"
            : margin < 0 ? "var(--err)" : (c.t_min_mm && margin < c.t_min_mm * 0.2) ? "#b06d12" : "var(--ink-2)";
          const dqTip = dqTooltip(c.dq);
          const selected = c.cml_id === selId;
          const gov = c.cml_no === govCml;
          return (
            <div key={c.cml_id} role="button" tabIndex={0} onClick={() => onSelect(c)}
                 onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onSelect(c); }}
                 aria-label={`CML ${c.cml_no}`}
                 className="grid items-center gap-1.5 px-2.5 py-1.5 border-b font-mono tabular-nums cursor-pointer transition-colors hover:bg-[rgba(var(--accent-rgb)_/_0.05)]"
                 style={{ gridTemplateColumns: GRID, fontSize: 10.5, borderColor: "var(--hairline)",
                          background: selected ? "rgba(31,78,121,0.07)" : undefined,
                          borderLeft: `2px solid ${gov ? "var(--accent)" : selected ? "var(--ink-3)" : "transparent"}` }}>
              <span className="truncate" style={{ color: "var(--ink-1)", fontWeight: gov || selected ? 700 : 400 }}>{c.cml_no}</span>
              <span className="text-right">{fmtMm(c.t_actual_mm)}</span>
              <span className="text-right" style={{ color: "var(--ink-3)" }}>{c.t_min_mm && c.t_min_mm > 0 ? c.t_min_mm.toFixed(2) : "–"}</span>
              <span className="text-right" style={{ fontWeight: 700, color: marginColor }}>
                {margin != null ? `${margin >= 0 ? "+" : "−"}${Math.abs(margin).toFixed(2)}` : "–"}
              </span>
              <span className="text-right" style={{ color: "var(--ink-3)" }}>{fmtRate(c.governing_rate_mm_yr)}</span>
              <span className="text-right" style={{ color: "var(--ink-2)" }}>{fmtYr(c.remaining_life_years)}</span>
              <span className="text-right">
                {dqTip && (
                  <span role="img" aria-label="ค่าที่น่าสงสัย" title={dqTip} style={{ color: "var(--warn)" }}>
                    <TriangleAlert size={11} />
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── RIGHT TOP — selected CML's history table + trend ──
function CmlHistoryPanel({ c, nextDue }: { c: CmlRow; nextDue?: string | null }): JSX.Element {
  const rev = [...c.history].filter((h) => h.mm > 0).reverse();
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono" style={{ fontSize: 13, color: "var(--ink-1)", fontWeight: 700 }}>{c.cml_no}</span>
        <span className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: ".08em", color: "var(--ink-4)" }}>{c.cml_type}</span>
      </div>
      <RemainingLifeProjection c={c} nextDue={nextDue} />
      {rev.length > 0 && (
        <div className="mt-3 max-h-[110px] overflow-auto">
          {rev.map((h, i) => (
            <div key={i} className="flex items-center justify-between py-0.5 font-mono tabular-nums" style={{ fontSize: 10.5 }}>
              <span style={{ color: "var(--ink-3)" }}>{h.date.slice(0, 10)}</span>
              <span style={{ color: "var(--ink-1)" }}>{fmtMm(h.mm)} mm</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── RIGHT BOTTOM — live what-if calculator ──
const YR_MS = 365.25 * 24 * 3600 * 1000;

/** History rows -> the `readings` the what-if endpoint takes.
 *
 *  Two things happen here and both used to be one-liners at the call site,
 *  where only one of them was right.
 *
 *  The date is narrowed to YYYY-MM-DD. On the wire a history row's date is a
 *  datetime ("2021-06-13T00:00:00"); WhatIfReading asks for a plain ISO date.
 *  Passing the timestamp through 422'd EVERY request, so the calculator below
 *  showed "คำนวณไม่สำเร็จ — ลองใหม่" and three em dashes for its entire life in
 *  production. The server now normalises timestamps too — this is the other
 *  half, because a client should send what the contract asks for rather than
 *  rely on the server being forgiving.
 *
 *  mm <= 0 rows are dropped: a 0.0 is the no-reading marker used when a point
 *  was inaccessible that survey, not a measurement of zero thickness (same
 *  policy as the corrosion kernel and the register, 2026-07-07). Feeding it in
 *  would invent a catastrophic loss rate out of a missed inspection. */
export function whatIfReadings(
  history: { date: string; mm: number }[],
): { date: string; mm: number }[] {
  return history
    .filter((h) => h.mm > 0)
    .map((h) => ({ date: h.date.slice(0, 10), mm: h.mm }));
}

function Calculator({ cml }: { cml: CmlRow }): JSX.Element {
  const realTMin = cml.t_min_mm && cml.t_min_mm > 0 ? cml.t_min_mm : null;

  const [crText, setCrText] = useState("");
  const [tMinText, setTMinText] = useState(realTMin != null ? String(realTMin) : "");
  const [intervalText, setIntervalText] = useState("");
  const [result, setResult] = useState<WhatIfResult | null>(null);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Switching CML resets every field to that CML's real values — a stale
  // what-if from the PREVIOUS CML must never linger on screen.
  useEffect(() => {
    setCrText("");
    setTMinText(realTMin != null ? String(realTMin) : "");
    setIntervalText("");
    setResult(null);
    setErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cml.cml_id]);

  const crNum = parseNum(crText);
  const tMinNum = parseNum(tMinText);
  const intervalNum = parseNum(intervalText);
  const crAssumed = crNum != null;
  const tMinAssumed = tMinNum != null && realTMin != null && tMinNum !== realTMin;

  const readings = useMemo(() => whatIfReadings(cml.history), [cml.history]);

  // Live what-if — debounced 250ms so a fast typist doesn't fire a request
  // per keystroke. Same kernel the real /corrosion analysis uses (calc.py);
  // this component never computes remaining life itself.
  useEffect(() => {
    if (readings.length === 0 || tMinNum == null || !(tMinNum > 0)) { setResult(null); return; }
    let cancelled = false;
    const h = setTimeout(() => {
      setPending(true);
      apiClient.whatIf({
        readings,
        t_min_mm: tMinNum,
        t_retirement_mm: cml.t_retirement_mm ?? 0,
        override_cr_mm_yr: crNum,
      }).then((res) => { if (!cancelled) { setResult(res); setErr(null); } })
        .catch(() => { if (!cancelled) setErr("คำนวณไม่สำเร็จ — ลองใหม่"); })
        .finally(() => { if (!cancelled) setPending(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(h); };
  }, [readings, tMinNum, crNum, cml.t_retirement_mm]);

  function reset(): void {
    setCrText("");
    setTMinText(realTMin != null ? String(realTMin) : "");
    setIntervalText("");
  }

  // Fields the SERVER flags as recomputed from the CR override (see calc.py
  // WhatIfBody.overridden). A t-min override isn't in that list (the
  // endpoint has no notion of "the real t-min" to compare against — it just
  // computes with whatever t-min it's given) so we also mark
  // remaining-life/years-to-retirement assumed locally whenever t-min was
  // hand-edited, matching the honest "assumed vs derived" rule even where
  // the backend can't tell us.
  const serverOverridden = new Set(result?.overridden ?? []);
  const rlAssumed = serverOverridden.has("remaining_life_years") || tMinAssumed;
  const ytrAssumed = serverOverridden.has("years_to_retirement") || tMinAssumed;

  // "Next due" here is plain date arithmetic (last survey + the interval
  // you're trying), NOT a re-derivation of the code's ½-remaining-life-
  // capped-at-10yr rule — that rule lives entirely in
  // server/routers/corrosion.py::_next_inspection and is already surfaced
  // as the asset's real next_inspection_due. This field lets you explore a
  // DIFFERENT interval and see where it lands; it never claims to BE the
  // governing interval.
  const lastSurvey = readings.length ? readings[readings.length - 1].date : null;
  const hypotheticalDue = (lastSurvey && intervalNum != null && intervalNum > 0)
    ? new Date(new Date(lastSurvey).getTime() + intervalNum * YR_MS).toISOString().slice(0, 10)
    : null;

  const whatIfProjection: WhatIfProjection | null = (result && crAssumed && result.remaining_life_years != null)
    ? { rateMmYr: result.governing_rate_mm_yr ?? crNum ?? 0, remainingLifeYears: result.remaining_life_years }
    : null;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono uppercase" style={{ fontSize: 9.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>
          เครื่องคำนวณ what-if
        </span>
        <button onClick={reset} title="รีเซ็ตทุกช่องเป็นค่าจริงของ CML นี้"
                className="flex items-center gap-1 font-mono uppercase px-2 py-1 hover:text-[var(--accent)]"
                style={{ fontSize: 9, letterSpacing: ".08em", color: "var(--ink-3)", border: "1px solid var(--hairline)", cursor: "pointer" }}>
          <RotateCcw size={11} /> รีเซ็ตเป็นค่าจริง
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <CalcField label="CR override (mm/yr)" value={crText} onChange={setCrText}
                   placeholder={fmtRate(cml.governing_rate_mm_yr)} assumed={crAssumed} step="0.001" />
        <CalcField label="t-min (mm)" value={tMinText} onChange={setTMinText} assumed={tMinAssumed} step="0.01" />
        <CalcField label="interval (yr)" value={intervalText} onChange={setIntervalText} assumed={intervalNum != null} step="0.5" />
      </div>

      {err && <div className="mt-2 font-mono" style={{ fontSize: 10.5, color: "var(--err)" }}>{err}</div>}
      {(tMinNum == null || !(tMinNum > 0)) && !err && (
        <div className="mt-2 font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>กรอก t-min เพื่อคำนวณ</div>
      )}

      <div className="grid grid-cols-3 gap-2 mt-3">
        <CalcStat label="Remaining life" value={result ? `${fmtYr(result.remaining_life_years)} yr` : pending ? "…" : "—"} assumed={rlAssumed} testId="desk-remaining-life" />
        <CalcStat label="Years to retirement" value={result ? `${fmtYr(result.years_to_retirement)} yr` : pending ? "…" : "—"} assumed={ytrAssumed} testId="desk-years-to-retirement" />
        <CalcStat label="Next due (สมมติ)" value={hypotheticalDue ?? "—"} assumed={intervalNum != null} />
      </div>

      <div className="mt-3 flex-1 min-h-0">
        <RemainingLifeProjection c={cml} whatIf={whatIfProjection} />
      </div>
    </div>
  );
}

function CalcField({ label, value, onChange, placeholder, assumed, step }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; assumed: boolean; step: string;
}): JSX.Element {
  return (
    <label className="block">
      <div className="font-mono uppercase mb-1" style={{ fontSize: 8, letterSpacing: ".08em", color: "var(--ink-4)" }}>{label}</div>
      <input type="number" step={step} value={value} placeholder={placeholder}
             aria-label={label}
             onChange={(e) => onChange(e.target.value)}
             className="w-full font-mono tabular-nums px-2 py-1"
             style={{ fontSize: 11.5, color: "var(--ink-1)", background: "var(--paper)",
                      border: `1px solid ${assumed ? "#7C3AED" : "var(--hairline)"}` }} />
    </label>
  );
}

function CalcStat({ label, value, assumed, testId }: { label: string; value: string; assumed: boolean; testId?: string }): JSX.Element {
  return (
    <div className="px-2 py-1.5" style={{ border: "1px solid var(--hairline)", background: "var(--paper-2)" }}>
      <div className="font-mono uppercase" style={{ fontSize: 7.5, letterSpacing: ".08em", color: "var(--ink-4)" }}>{label}</div>
      <div className="font-mono tabular-nums" data-testid={testId} style={{ fontSize: 13, fontWeight: 700, color: assumed ? "#7C3AED" : "var(--ink-1)" }}>{value}</div>
      {assumed && <div className="font-mono uppercase" style={{ fontSize: 7, letterSpacing: ".06em", color: "#7C3AED" }}>สมมติฐาน</div>}
    </div>
  );
}

function CenterNote({ children, tone }: { children: React.ReactNode; tone?: "err" }): JSX.Element {
  return (
    <div className="absolute inset-0 flex items-center justify-center" style={{ background: "var(--paper)" }}>
      <div className="font-mono uppercase" style={{ fontSize: 12, letterSpacing: ".22em", color: tone === "err" ? "var(--err)" : "var(--ink-3)" }}>
        {children}
      </div>
    </div>
  );
}
